import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Settings, LogOut, Shield, ChevronDown } from 'lucide-react';

export function Header({ navigate, title }) {
  const { user, profile, isAdmin, signOut } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const initials = profile?.username?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?';
  const displayName = profile?.username || 'User';
  
  // Get avatar - check profile, then localStorage
  const avatarUrl = profile?.avatar_url || (user?.id ? localStorage.getItem(`sage_avatar_${user.id}`) : null);

  return (
    <header className="flex items-center justify-between p-4 border-b border-[#1a1a24] bg-[#08080c]">
      {title ? (
        <h1 className="font-mono text-xs text-[#00d4ff] uppercase tracking-[0.2em]">{title}</h1>
      ) : (
        <div>
          <p className="text-sm text-[#e8e8f0]">
            {getGreeting()}, <span className="text-[#00d4ff]">{displayName}</span>
          </p>
        </div>
      )}
      
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center gap-2 group"
          data-testid="profile-avatar-btn"
        >
          {avatarUrl ? (
            <img 
              src={avatarUrl} 
              alt="Profile" 
              className="w-8 h-8 rounded-full object-cover border border-[#00d4ff]/30 group-hover:border-[#00d4ff]/60 transition-colors"
            />
          ) : (
            <div className="w-8 h-8 border border-[#00d4ff]/30 flex items-center justify-center bg-[#00d4ff]/5 group-hover:border-[#00d4ff]/60 transition-colors">
              <span className="font-mono text-sm text-[#00d4ff]">{initials}</span>
            </div>
          )}
          <ChevronDown className={`w-3 h-3 text-[#4a4a60] transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>

        {showDropdown && (
          <div 
            className="absolute right-0 top-full mt-2 w-48 bg-[#0d0d12] border border-[#1a1a24] shadow-xl z-50"
            style={{ boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)' }}
            data-testid="profile-dropdown"
          >
            <div className="px-4 py-3 border-b border-[#1a1a24]">
              <p className="font-mono text-xs text-[#e8e8f0] truncate">{displayName}</p>
              <p className="text-[10px] text-[#4a4a60] truncate mt-0.5">{user?.email}</p>
            </div>

            <button
              onClick={() => { navigate('profile'); setShowDropdown(false); }}
              className="w-full px-4 py-3 text-left text-xs text-[#7a7a9a] hover:bg-[#111118] hover:text-[#e8e8f0] flex items-center gap-3 transition-colors"
              data-testid="dropdown-profile"
            >
              <div className="w-3.5 h-3.5 border border-[#4a4a60] flex items-center justify-center">
                <span className="text-[8px]">{initials}</span>
              </div>
              Profile
            </button>

            <button
              onClick={() => { navigate('settings'); setShowDropdown(false); }}
              className="w-full px-4 py-3 text-left text-xs text-[#7a7a9a] hover:bg-[#111118] hover:text-[#e8e8f0] flex items-center gap-3 transition-colors"
              data-testid="dropdown-settings"
            >
              <Settings className="w-3.5 h-3.5" />
              Settings
            </button>

            {isAdmin && (
              <button
                onClick={() => { navigate('admin'); setShowDropdown(false); }}
                className="w-full px-4 py-3 text-left text-xs text-[#00d4ff] hover:bg-[#00d4ff]/5 flex items-center gap-3 transition-colors"
                data-testid="dropdown-admin"
              >
                <Shield className="w-3.5 h-3.5" />
                Admin Panel
              </button>
            )}

            <div className="border-t border-[#1a1a24]" />
            <button
              onClick={handleSignOut}
              className="w-full px-4 py-3 text-left text-xs text-[#ff3366] hover:bg-[#ff3366]/5 flex items-center gap-3 transition-colors"
              data-testid="dropdown-logout"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
